import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useSite } from '@/context/SiteContext';
import { getRoleBasedRoute } from '@/services/authService';

const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
      <p className="mt-4 text-muted-foreground">Loading...</p>
    </div>
  </div>
);

export const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return children;
};

/**
 * RoleRoute - Guards a route so only users with one of the allowed roles can access it.
 * @param {string[]} allowedRoles - Array of role strings (e.g. ['doctor', 'nurse'])
 * @param {React.ReactNode} children - The page component to render
 */
export const RoleRoute = ({ allowedRoles, children }) => {
  const { isAuthenticated, hasRole, user, loading } = useAuth();

  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  if (!hasRole(allowedRoles)) {
    // Redirect to the user's role-based default landing page
    const fallback = getRoleBasedRoute(user?.role, user?.roles);
    return <Navigate to={fallback} replace />;
  }

  return children;
};

// AdminRoute kept for backward-compatibility; delegates to RoleRoute
export const AdminRoute = ({ children }) => (
  <RoleRoute allowedRoles={['admin', 'siteadmin', 'tenantadmin', 'superadmin']}>{children}</RoleRoute>
);

/**
 * ServiceRoute - Guards a route by role AND required site service.
 * If the active site doesn't have the required service enabled, redirects to dashboard.
 */
export const ServiceRoute = ({ allowedRoles, requiredService, children }) => {
  const { isAuthenticated, hasRole, user, loading } = useAuth();
  const { activeSite, loading: siteLoading } = useSite();

  if (loading || siteLoading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  if (!hasRole(allowedRoles)) {
    const fallback = getRoleBasedRoute(user?.role, user?.roles);
    return <Navigate to={fallback} replace />;
  }

  // If site has services configured, check if the required service is enabled
  const siteServices = activeSite?.services || [];
  if (siteServices.length > 0 && requiredService && !siteServices.includes(requiredService)) {
    const fallback = getRoleBasedRoute(user?.role, user?.roles);
    return <Navigate to={fallback} replace />;
  }

  return children;
};
